import { ChangeEvent, useEffect, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type { FieldValidation } from '@rjsf/utils';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
} from '@material-ui/core';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import type { BuildTemplateResponse } from '@openchoreo/backstage-plugin-common';

type ModelsBuildTemplate = BuildTemplateResponse;

/*
 Schema for the Custom Field Explorer
*/
export const BuildTemplatePickerSchema = {
  returnValue: { type: 'string' },
};

/*
 This is the actual component that will get rendered in the form
*/
export const BuildTemplatePicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  formContext,
  idSchema,
  uiSchema,
  schema,
}: FieldExtensionComponentProps<string>) => {
  const [buildTemplates, setBuildTemplates] = useState<ModelsBuildTemplate[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  // Get the namespace name from form context
  const namespaceName = formContext.formData?.namespace_name;

  useEffect(() => {
    let ignore = false;
    const fetchBuildTemplates = async () => {
      if (!namespaceName) {
        setBuildTemplates([]);
        // Clear templates from form context when no namespace
        if (formContext.buildTemplates) {
          delete formContext.buildTemplates;
        }
        return;
      }

      // Extract the actual namespace name from the entity reference format
      // e.g., "domain:default/my-namespace" -> "my-namespace"
      const extractNsName = (fullNsName: string): string => {
        const parts = fullNsName.split('/');
        return parts[parts.length - 1];
      };

      const nsName = extractNsName(namespaceName);

      setLoading(true);
      setError(null);

      try {
        const baseUrl = await discoveryApi.getBaseUrl('openchoreo');
        // Use fetchApi which automatically injects Backstage + IDP tokens
        const response = await fetchApi.fetch(
          `${baseUrl}/build-templates?namespaceName=${encodeURIComponent(
            nsName,
          )}`,
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const templates = await response.json();
        if (!ignore) setBuildTemplates(templates);

        // Store templates in form context so other components can access them
        formContext.buildTemplates = templates;
      } catch (err) {
        setError(`Failed to fetch build templates: ${err}`);
        setBuildTemplates([]);
        // Clear templates from form context on error
        if (formContext.buildTemplates) {
          delete formContext.buildTemplates;
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBuildTemplates();
    return () => {
      ignore = true;
    };
  }, [namespaceName, discoveryApi, formContext, fetchApi]);

  const handleChange = (event: ChangeEvent<{ value: unknown }>) => {
    onChange(event.target.value as string);
    // Ensure templates are still in form context when selection changes
    if (buildTemplates.length > 0) {
      formContext.buildTemplates = buildTemplates;
    }
  };

  return (
    <FormControl
      fullWidth
      margin="normal"
      error={!!rawErrors?.length}
      required={required}
    >
      <InputLabel id={`${idSchema?.$id}-label`}>
        {uiSchema?.['ui:title'] || schema.title || 'Build Template'}
      </InputLabel>
      <Select
        labelId={`${idSchema?.$id}-label`}
        value={formData || ''}
        onChange={handleChange}
        disabled={loading || !namespaceName}
      >
        {loading && (
          <MenuItem disabled>
            <CircularProgress size={20} style={{ marginRight: 8 }} />
            Loading build templates...
          </MenuItem>
        )}
        {!loading && buildTemplates.length === 0 && !error && (
          <MenuItem disabled>
            {namespaceName
              ? 'No build templates available'
              : 'Select a namespace first'}
          </MenuItem>
        )}
        {!loading &&
          buildTemplates.map(template => (
            <MenuItem key={template.name} value={template.name}>
              {template.name}
            </MenuItem>
          ))}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
      {rawErrors?.length ? (
        <FormHelperText>{rawErrors.join(', ')}</FormHelperText>
      ) : null}
      {schema.description && !rawErrors?.length && (
        <FormHelperText>{schema.description}</FormHelperText>
      )}
    </FormControl>
  );
};

/*
 This is a validation function that will run when the form is submitted.
*/
export const buildTemplatePickerValidation = (
  value: string,
  validation: FieldValidation,
) => {
  if (!value || value.trim() === '') {
    validation.addError('Build template is required when using built-in CI');
  }
};
